import React, { useState, useCallback, useEffect } from "react";
import { IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { IconPinned, IconPinnedFilled } from "@tabler/icons-react";
import { fetchApi } from "./FetchApi";
import { toggleFavorite } from "../services/UserService";
import { notifyGlobal } from "../services/ServiceUtils";
import { useAuth } from "./AuthProvider";
import { useNavigate } from "react-router-dom";
import { useFavoriteState } from "../context/FavoriteStateContext";

/**
 * Przycisk ulubionych – jeden endpoint toggle, optymistyczna aktualizacja.
 * @param {number} listingId - ID ogłoszenia
 * @param {boolean} isFav - Czy ogłoszenie jest w ulubionych (z props rodzica)
 * @param {(payload: { listingId: number; isFav: boolean }) => void} [onFavoriteChange] - Callback po zmianie
 */
export default function Favorite({ listingId, isFav, onFavoriteChange }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { hasFavorite, getFavorite, setFavorite } = useFavoriteState();

  const [isFavorited, setIsFavorited] = useState(() => getFavorite(listingId, !!isFav));
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!hasFavorite(listingId)) {
      setFavorite(listingId, !!isFav);
      setIsFavorited(!!isFav);
      return;
    }
    setIsFavorited(getFavorite(listingId, !!isFav));
  }, [getFavorite, hasFavorite, isFav, listingId, setFavorite]);

  const trackInteraction = useCallback(
    async (interactionType) => {
      try {
        let sessionId = localStorage.getItem("viewSessionId");
        if (!sessionId) {
          sessionId = "session_" + Math.random().toString(36).substring(2, 15);
          localStorage.setItem("viewSessionId", sessionId);
        }
        const searchSessionId = localStorage.getItem("lastSearchSessionId");
        await fetchApi("/behavior/interaction", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            listingId,
            sessionId,
            interactionType,
            searchSessionId: searchSessionId || null,
          }),
        });
      } catch (err) {
        console.error("Error tracking interaction:", err);
      }
    },
    [listingId]
  );

  const handleClick = useCallback(
    async (e) => {
      e.stopPropagation();
      if (!isLoggedIn) {
        navigate("/sign/in");
        return;
      }
      if (isProcessing) return;

      const nextFavorited = !isFavorited;
      setIsFavorited(nextFavorited);
      setFavorite(listingId, nextFavorited);
      setIsProcessing(true);

      try {
        const { data, meta } = await toggleFavorite(listingId);
        const isApiError =
          meta &&
          ["ERROR", "WARNING"].includes(String(meta?.type || "").toUpperCase());

        if (isApiError) {
          setIsFavorited(!nextFavorited);
          setFavorite(listingId, !nextFavorited);
          notifyGlobal({
            level: "error",
            message: nextFavorited
              ? "Nie udało się dodać do ulubionych"
              : "Nie udało się usunąć z ulubionych",
            details: meta?.message,
          });
        } else {
          const favorited = data?.favorited ?? nextFavorited;
          setIsFavorited(favorited);
          setFavorite(listingId, favorited);
          trackInteraction(favorited ? "FAVORITE_ADD" : "FAVORITE_REMOVE");
          onFavoriteChange?.({ listingId, isFav: favorited });
        }
      } catch (err) {
        console.error("Error toggling favorite:", err);
        setIsFavorited(!nextFavorited);
        setFavorite(listingId, !nextFavorited);
        notifyGlobal({
          level: "error",
          message: "Nie udało się zaktualizować ulubionych",
          details: err?.message,
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [
      isFavorited,
      isLoggedIn,
      isProcessing,
      listingId,
      navigate,
      onFavoriteChange,
      setFavorite,
      trackInteraction,
    ]
  );

  const iconSize = isMobile ? 20 : 24;
  const label = isFavorited ? "Usuń z ulubionych" : "Dodaj do ulubionych";

  if (!isLoggedIn) {
    return (
      <Tooltip title="Zaloguj się, aby dodać do ulubionych">
        <IconButton
          aria-label={label}
          onClick={(e) => {
            e.stopPropagation();
            navigate("/sign/in");
          }}
          size="small"
          sx={{ color: "action.disabled" }}
        >
          <IconPinned size={iconSize} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={label}>
      <IconButton
        aria-label={label}
        onClick={handleClick}
        disabled={isProcessing}
        size="small"
        sx={{
          color: isFavorited ? "primary.main" : "action.disabled",
        }}
      >
        {isFavorited ? (
          <IconPinnedFilled size={iconSize} />
        ) : (
          <IconPinned size={iconSize} />
        )}
      </IconButton>
    </Tooltip>
  );
}
